import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

// Slug helper — normalise labels en test-ids stables (sans double-dash en début/fin).
const slug = (s) => s.toLowerCase().replace(/\W+/g, "-").replace(/^-+|-+$/g, "");

// Structure: flat items OR groups with `children` sub-items.
const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/cours", label: "Nos cours" },
  { to: "/planning", label: "Planning" },
  { to: "/competition", label: "Compétition" },
  {
    label: "L'école",
    matches: ["/ecole", "/professeurs", "/tarifs", "/actualites", "/sponsoring", "/assurances", "/faq"],
    children: [
      { to: "/ecole", label: "L'école" },
      { to: "/professeurs", label: "Notre équipe" },
      { to: "/tarifs", label: "Tarifs" },
      { to: "/actualites", label: "Actualités" },
      { to: "/sponsoring", label: "Sponsoring" },
      { to: "/assurances", label: "Assurance maladie" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  {
    label: "Événements",
    matches: ["/evenements", "/prestations", "/stages", "/spectacles", "/dates-importantes"],
    children: [
      { to: "/dates-importantes", label: "Dates importantes" },
      { to: "/spectacles", label: "Spectacles" },
      { to: "/evenements/anniversaires", label: "Anniversaires" },
      { to: "/evenements/location-salle", label: "Location de salle" },
      { to: "/evenements/mariage", label: "Danse de mariage" },
      { to: "/evenements/workshops-heels", label: "Workshops heels" },
      { to: "/stages", label: "Stages" },
    ],
  },
  { to: "/contact", label: "Contact" },
  { to: "/espace", label: "Mon espace" },
];

/** Desktop dropdown item — accessible : hover + focus + click. */
function DropdownItem({ item, currentPath }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const isActive = (item.matches || []).some((m) => currentPath.startsWith(m));

  const scheduleClose = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`nav-group-${slug(item.label)}`}
        aria-expanded={open}
        aria-haspopup="true"
        className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-1 ${
          isActive ? "text-white" : "text-[#64748B] hover:text-[#0F1B2D]"
        }`}
        style={isActive ? { background: "var(--primary)", color: "#fff" } : {}}
      >
        {item.label}
        <ChevronDown size={14} style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "" }} />
      </button>
      {open && (
        <div
          data-testid={`nav-menu-${slug(item.label)}`}
          className="absolute left-0 top-full mt-1 min-w-[220px] rounded-2xl bg-white shadow-xl border border-[var(--border-soft)] overflow-hidden z-50"
          role="menu"
        >
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              role="menuitem"
              data-testid={`nav-sub-${c.to.replace(/\//g, "") || "home"}`}
              onClick={() => setOpen(false)}
              className={({ isActive: sa }) =>
                `block px-4 py-2.5 text-sm transition-colors ${sa ? "font-semibold" : "font-medium"} hover:bg-[var(--primary-tint)]`
              }
              style={({ isActive: sa }) => sa ? { color: "var(--primary)" } : { color: "#0F1B2D" }}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState({});
  const loc = useLocation();
  const navigate = useNavigate();

  // Handler pour le CTA "Inscription" — force le retour au menu principal
  // même quand on est déjà sur /inscription?type=X
  const goInscription = (e) => {
    e.preventDefault();
    setOpen(false);
    if (loc.pathname === "/inscription") {
      // Déjà sur la page inscription : hard reload vers l'URL de base
      // (garantit reset du state du formulaire + clear des query params)
      window.location.href = "/inscription";
    } else {
      navigate("/inscription");
    }
  };
  useEffect(() => { setOpen(false); setExpandedMobile({}); }, [loc.pathname]);

  const toggleMobile = (label) => setExpandedMobile((s) => ({ ...s, [label]: !s[label] }));

  return (
    <header
      data-testid="navbar"
      className="fixed left-0 right-0 z-50 backdrop-blur-md"
      style={{ top: "var(--annonce-h, 0px)", background: "rgba(255,255,255,0.85)", borderBottom: "1px solid var(--border-soft)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img loading="eager" fetchPriority="high" decoding="async" width="112" height="56"
            src="/img/9ab2fa8bea.webp"
            alt="1000 ARTS"
            className="h-12 md:h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {NAV.map((item) => {
            if (item.children) return <DropdownItem key={item.label} item={item} currentPath={loc.pathname} />;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 xl:px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive ? "text-white" : "text-[#64748B] hover:text-[#0F1B2D]"
                  }`
                }
                style={({ isActive }) => isActive ? { background: "var(--primary)", color: "#fff" } : {}}
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <Link to={{ pathname: "/inscription", search: "" }} onClick={goInscription} data-testid="nav-cta-inscription" className="hidden lg:inline-flex btn-primary" style={{ padding: "12px 22px" }}>
          Inscription
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#0F1B2D]"
          onClick={() => setOpen((s) => !s)}
          aria-label="Menu"
          aria-expanded={open}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden px-6 pb-6 border-t border-[var(--border-soft)] flex flex-col gap-1 pt-5 bg-white">
          {NAV.map((item) => {
            if (item.children) {
              const isExpanded = !!expandedMobile[item.label];
              return (
                <div key={item.label} className="border-b border-[var(--border-soft)] last:border-0">
                  <button
                    type="button"
                    onClick={() => toggleMobile(item.label)}
                    data-testid={`mobile-nav-group-${slug(item.label)}`}
                    aria-expanded={isExpanded}
                    className="w-full flex items-center justify-between py-3 text-[15px] font-medium text-[#0F1B2D]"
                  >
                    <span>{item.label}</span>
                    <ChevronDown size={16} style={{ transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "" }} />
                  </button>
                  {isExpanded && (
                    <div className="pl-4 pb-3 flex flex-col gap-2">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`mobile-nav-sub-${c.to.replace(/\//g, "") || "home"}`}
                          className="text-[14px] py-1.5 text-[#64748B] hover:text-[var(--primary)]"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.to.replace("/", "") || "home"}`}
                className="text-[15px] font-medium text-[#0F1B2D] py-2.5 border-b border-[var(--border-soft)] last:border-0"
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            );
          })}
          <Link to={{ pathname: "/inscription", search: "" }} onClick={goInscription} data-testid="mobile-nav-cta" className="btn-primary justify-center mt-4">
            Inscription
          </Link>
        </div>
      )}
    </header>
  );
}
